/**
 * TransitionPage — V103-10-6: the government-cloud move (ADR-0069 + owner
 * decisions 2026-10-06; design v103-programs-ui round 4, screen D).
 * Route: /w/:slug/app/programs/:program/scope/move?step=target|changes|plan|reporting&p=<program>
 *
 *   1 Target   provider tiles (unverified-data label + Verify), components that
 *              move, cutover date with the live "notice due by" (rules, not
 *              constants) and a too-soon warning. Nothing changes in the
 *              programs until cutover.
 *   2 Changes  one tab per participating program ("✓ viewed"; the plan opens
 *              at N of N viewed): readiness today vs projected, inheritance
 *              proposals to confirm, components (inline "Change" that rewrites
 *              the draft target and recomputes the gap with NO milestone side
 *              effects; "Keep current" recounts and reads "stays on <provider>",
 *              acceptance #3), third parties, projected milestones (already
 *              planned / not needed).
 *   3 Plan     milestones (work items, never POA&M): status and action in
 *              separate columns, inline owner / date edits; carried rows read
 *              "Already planned" (acceptance #5).
 *   4 Reports  per program: FedRAMP SCN (JSON), GovRAMP change notice (record
 *              as sent), signed target drafts; the cutover gate split per
 *              program. There is no override.
 *
 * Reads: any member. Writes: owner/admin or the program lead; cutover
 * owner/admin (server-enforced; refusals shown inline).
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea, Toast } from '@aigovops/design-system';
import { AskAdmin } from '../../components/AskAdmin';
import { getProgramManifest, requirementLabel, type BoundaryComponent, type PlanItemView, type TransitionGap, type ProjectedMilestone } from '@aigovops/core';
import { useWorkspace } from '../../lib/workspace';
import { usePrograms } from '../../lib/workspace-navigation';
import { addCoverageDocument, listProgramExternalServices, listWorkspaceMembers } from '../../lib/api';
import {
  cancelTransition, completeCutover, createComponent, createTransition, decideProposal, ensureTargetService, generateProposals, getCatalog, listDrafts, getGap, getPlan, getScn,
  getScope, getTargetScope, getTransition, issueDraft, joinTransition, listComponents, listNotices, planTransition, scnDownloadHref, sendNotice,
  setTargetScope, startCutover, updatePlanItem, updateTransition, type CatalogOfferingView,
} from '../../lib/boundary-api';
import { KIND_LABEL, programLabel, providerLabel, usesComponentBoundary } from '../../lib/boundary-model';
import {
  CHANGE_HEADING, choicesFrom, collapseFamilies, groupObjectives, longDate, markViewed, noticeDue, openProposals, planStatus, projectedLabel, readViewed, shortDate,
  targetKey, targetScope, type MoveChoice,
} from '../../lib/transition-model';
import { invalidationMap } from '../../lib/query-keys';
import '../today.css';
import { planGateText } from '../../lib/plan-gate';
import '../../components/programs/component-boundary.css';

type Step = 'target' | 'changes' | 'plan' | 'reporting';
const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'target', label: 'Target' }, { key: 'changes', label: 'What changes' }, { key: 'plan', label: 'Schedule' }, { key: 'reporting', label: 'Reports & cutover' },
];
const errMsg = (e: unknown, f: string) => (e instanceof Error && e.message ? e.message : f);

export function TransitionPage() {
  const { program = '' } = useParams<{ program: string }>();
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const m = getProgramManifest(program);
  const cards = usePrograms(slug).data?.programs ?? [];
  const tr = useQuery({ queryKey: ['transition', slug, program], queryFn: () => getTransition(slug, program), enabled: !!m });
  const catalog = useQuery({ queryKey: ['transition', slug, program, 'catalog'], queryFn: () => getCatalog(slug, program), enabled: !!m });
  const components = useQuery({ queryKey: ['boundary', slug, 'components'], queryFn: () => listComponents(slug, program), enabled: !!m });
  const services = useQuery({ queryKey: ['transition', slug, program, 'services'], queryFn: () => listProgramExternalServices(slug, program), enabled: !!m });
  const members = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000 });
  const boundaryPrograms = cards.filter((c) => usesComponentBoundary(getProgramManifest(c.program)));
  const standing = useQueries({ queries: boundaryPrograms.map((c) => ({ queryKey: ['boundary', slug, 'scope', c.program, null], queryFn: () => getScope(slug, c.program, null) })) });
  const [toast, setToast] = useState<{ message: string; details?: string; action?: { label: string; onClick: () => void }; v: number } | null>(null);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const t = tr.data?.transition ?? null;
  const participants = t?.participants ?? [];
  const live = !!t && ['draft', 'planned', 'cutover'].includes(t.state);
  const requested = sp.get('step') as Step | null;
  const step: Step = !live ? 'target' : requested && STEPS.some((s) => s.key === requested) ? requested : 'changes';

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['transition', slug] }), qc.invalidateQueries({ queryKey: ['boundary', slug] }),
    ...invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })),
  ]);
  const say = (message: string, details?: string) => setToast({ message, details, v: Date.now() });
  const goStep = (s: Step, extra: Record<string, string> = {}) => { setSp({ step: s, ...extra }); window.setTimeout(() => heading.current?.focus(), 0); };

  if (!m) return <Navigate to={`${prefix}/programs`} replace />;
  if (tr.isLoading || components.isLoading) return <div className="gp-program-shell"><Spinner label="Loading the move" /></div>;
  if (tr.error || !tr.data) return <div className="gp-program-shell"><Alert variant="error"><p>The move could not load.</p><Button onClick={() => void tr.refetch()}>Retry</Button></Alert></div>;

  const canWrite = tr.data.can_write;
  const all = components.data?.components ?? [];
  const names = new Map((services.data?.external_services ?? []).map((s) => [s.external_service_id, s.name]));
  const offering = t ? catalog.data?.offerings.find((o) => o.offering_key === t.catalog_offering_key) ?? null : null;
  const viewedNow = t ? readViewed(t.transition_id) : new Set<string>();
  const allViewed = participants.every((p) => viewedNow.has(p.program));
  const done = (s: Step) => STEPS.findIndex((x) => x.key === s) < STEPS.findIndex((x) => x.key === step);
  const locked = (s: Step) => !live || ((s === 'plan' || s === 'reporting') && t?.state === 'draft' && !allViewed);

  return (
    <div className="gp-program-shell gp-tr">
      <nav className="gp-crumbs" aria-label="Breadcrumb">
        <Link to={`${prefix}/programs`}>Programs</Link><span aria-hidden="true">/</span>
        <Link to={`${prefix}/programs/${program}/scope`}>{`${programLabel(program)} boundary`}</Link><span aria-hidden="true">/</span>
        <span aria-current="page">Government cloud move</span>
      </nav>
      <PageHeader variant="app" headingId="tr-h1" title={t && live ? `Move to ${offering?.offering_name ?? names.get(t.target_external_service_id) ?? 'the target provider'}` : 'Move to a government cloud'}
        subtitle={t && live ? `${participants.map((p) => programLabel(p.program)).join(' + ')} · Cutover ${shortDate(t.planned_cutover_start)} · ${t.state === 'draft' ? 'Draft' : t.state === 'planned' ? 'Planned' : 'Cutover started'}` : undefined} />
      {error ? <Alert variant="error" onDismiss={() => setError(null)}>{error}</Alert> : null}
      {t && t.state === 'completed' ? <Alert variant="success"><p>{`The last move finished ${shortDate(t.completed_at)}. The target is now the standing boundary.`}</p></Alert> : null}
      {tr.data.joinable_transition_id && !t ? (
        <Alert variant="info"><p>A move is being drafted in this workspace. Join it to plan this program with it.</p>
          {canWrite ? <Button compact variant="primary" onClick={async () => { try { await joinTransition(slug, program); await refresh(); goStep('changes', { p: program }); } catch (e) { setError(errMsg(e, 'Could not join the move.')); } }}>Join the move</Button> : null}</Alert>
      ) : null}

      <nav aria-label="Move steps">
        <ol className="gp-tr-steps">
          {STEPS.map((s, i) => {
            const isLocked = locked(s.key) && s.key !== 'target';
            return (
              <li key={s.key}>
                {isLocked
                  ? <span className="gp-tr-step" aria-disabled="true" data-state="locked"><span className="gp-tr-n" aria-hidden="true">{i + 1}</span>{s.label}<span className="sr-only"> (after every program is viewed)</span></span>
                  : <Link className="gp-tr-step" to={`?step=${s.key}`} aria-current={step === s.key ? 'step' : undefined} data-state={done(s.key) ? 'done' : step === s.key ? 'current' : 'todo'}>
                      <span className="gp-tr-n" aria-hidden="true">{done(s.key) ? '✓' : i + 1}</span>{s.label}{done(s.key) ? <span className="sr-only"> completed</span> : null}
                    </Link>}
              </li>
            );
          })}
        </ol>
      </nav>

      <h2 className="sr-only" ref={heading} tabIndex={-1}>{STEPS.find((s) => s.key === step)!.label}</h2>

      {step === 'target' ? (
        <TargetStep slug={slug} program={program} prefix={prefix} canWrite={canWrite} transition={live ? t : null} offerings={catalog.data?.offerings ?? []} catalogLoading={catalog.isLoading}
          all={all} names={names} boundaryPrograms={boundaryPrograms.map((c) => c.program)} standing={standing.map((q) => q.data?.scope.components.map((c) => c.component_id) ?? [])}
          windows={tr.data.notice_windows} busy={busy} setBusy={setBusy} setError={setError} refresh={refresh}
          onDone={(first) => goStep('changes', { p: first })} say={say} />
      ) : null}

      {step === 'changes' && t ? (
        <ChangesStep slug={slug} prefix={prefix} transitionId={t.transition_id} state={t.state} participants={participants.map((p) => p.program)} active={sp.get('p') ?? participants[0]?.program ?? program}
          canWrite={canWrite} all={all} names={names} offeringKey={t.catalog_offering_key} targetServiceId={t.target_external_service_id} gateSha={tr.data.gate?.combined_sha256 ?? null} planGate={tr.data.plan_gate ?? null}
          setError={setError} refresh={refresh} say={say} onPlanned={() => goStep('plan')} onTab={(p) => setSp({ step: 'changes', p })} />
      ) : null}

      {step === 'plan' && t ? (
        <PlanStep slug={slug} prefix={prefix} program={program} transition={t} participants={participants} canWrite={canWrite}
          members={members.data?.members ?? []} setError={setError} refresh={refresh} say={say} gateSha={tr.data.gate?.combined_sha256 ?? null} onNext={() => goStep('reporting')} />
      ) : null}

      {step === 'reporting' && t ? (
        <ReportingStep slug={slug} prefix={prefix} program={program} transition={t} participants={participants} canWrite={canWrite} canCutover={tr.data.can_cutover}
          gate={tr.data.gate ?? null} setError={setError} refresh={refresh} say={say} role={role ?? 'viewer'} />
      ) : null}

      {t && (t.state === 'draft' || t.state === 'planned') && (step === 'target' || step === 'changes') ? (
        <CancelMove slug={slug} program={program} canWrite={canWrite} participants={participants.map((p) => p.program)} refresh={refresh} setError={setError}
          onCancelled={() => {
            // Round 3: the target plan reads the move too; refresh it, offer the way back, and land on the page heading.
            void qc.invalidateQueries({ queryKey: ['infra-plan', slug] });
            setToast({ message: 'Move cancelled', details: 'Its open milestones and change notices closed; nothing in the boundary changed.', action: { label: 'Back to the target plan', onClick: () => navigate(`${prefix}/programs/${program}/scope?view=plan`) }, v: Date.now() });
            window.setTimeout(() => document.getElementById('tr-h1')?.focus(), 0);
          }} />
      ) : null}

      {toast ? <Toast message={toast.message} details={toast.details} action={toast.action} version={toast.v} onDismiss={() => setToast(null)} /> : null}
    </div>
  );
}

/**
 * V105-6-7 R2-B2: cancel a draft or planned move (with a reason) — the only way out of a move a target
 * plan opened before it is locked (its feasibility checks, V105-7-2). Server: POST …/programs/:programId/transition/cancel,
 * owner / admin or the program lead (writeGate). Readers ask an admin. `#cancel` lands here with focus.
 */
export function CancelMove({ slug, program, canWrite, participants, refresh, setError, onCancelled }: {
  slug: string; program: string; canWrite: boolean; participants: string[]; refresh: () => Promise<unknown>; setError: (e: string | null) => void; onCancelled: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [busy, setBusy] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const viaLink = typeof location !== 'undefined' && location.hash === '#cancel';
  useEffect(() => { if (viaLink) opener.current?.focus(); }, [viaLink]);
  useEffect(() => { if (open) field.current?.focus(); }, [open]);
  const names = participants.map((p) => programLabel(p));
  const who = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] ?? programLabel(program);
  const close = () => { setOpen(false); setFieldError(''); window.setTimeout(() => opener.current?.focus(), 0); };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (reason.trim().length < 10) { setFieldError('Say why the move is cancelled (at least 10 characters).'); field.current?.focus(); return; }
    setBusy(true); setError(null);
    try { await cancelTransition(slug, program, reason.trim()); await refresh(); setOpen(false); onCancelled(); }
    catch (err) { setError(errMsg(err, 'The move could not be cancelled.')); }
    finally { setBusy(false); }
  };
  if (!canWrite) {
    // Readers see the ask only when they came to cancel (the plan's "Cancel the move" item links #cancel).
    return viaLink ? (
      <section id="cancel" className="gp-tr-cancel" aria-label="Cancel the move">
        <AskAdmin slug={slug} what="cancel the move" subject={`${programLabel(program)}: cancel the move`} lead="An owner, admin or the program lead" compact />
      </section>
    ) : null;
  }
  return (
    <section id="cancel" className="gp-tr-cancel" aria-labelledby="tr-cancel-h">
      <h3 id="tr-cancel-h" className="sr-only">Cancel the move</h3>
      {!open ? <Button ref={opener} compact variant="ghost" onClick={() => setOpen(true)}>Cancel the move</Button> : (
        <form className="gp-inline-form" data-esc-owner="" onSubmit={(e) => void submit(e)} noValidate aria-label="Cancel the move"
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}>
          <p>{`Cancels the move for ${who}: their milestones and change notices close. This can't be undone.`}</p>
          <Field id="tr-cancel-why" label="Why is the move cancelled?" required error={fieldError}>
            <Textarea ref={field} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setFieldError(''); }} />
          </Field>
          <span className="gp-actions">
            <Button compact variant="danger" type="submit" aria-busy={busy || undefined}>{busy ? 'Cancelling…' : names.length > 1 ? 'Cancel the move for all programs' : 'Cancel the move'}</Button>
            <Button compact variant="ghost" onClick={close}>Keep the move</Button>
          </span>
        </form>
      )}
    </section>
  );
}

// ---- 1 Target -------------------------------------------------------------------------------------

function TargetStep(props: {
  slug: string; program: string; prefix: string; canWrite: boolean; transition: { transition_id: string; catalog_offering_key: string | null; planned_cutover_start: string; participants: Array<{ program: string }>; state: string } | null;
  offerings: CatalogOfferingView[]; catalogLoading: boolean; all: BoundaryComponent[]; names: ReadonlyMap<string, string>; boundaryPrograms: string[]; standing: string[][];
  windows: Parameters<typeof noticeDue>[1]; busy: boolean; setBusy: (b: boolean) => void; setError: (e: string | null) => void; refresh: () => Promise<unknown>;
  onDone: (firstProgram: string) => void; say: (m: string, d?: string) => void;
}) {
  const { slug, program, prefix, canWrite, transition: t, offerings, all, names, boundaryPrograms, standing, windows } = props;
  const [offeringKey, setOfferingKey] = useState<string>(t?.catalog_offering_key ?? '');
  const [cutover, setCutover] = useState<string>(t ? t.planned_cutover_start.slice(0, 10) : '');
  const current = useMemo(() => [...new Set(standing.flat())], [standing]);
  const [moving, setMoving] = useState<Set<string>>(() => new Set(current));
  useEffect(() => { setMoving((s) => (s.size ? s : new Set(current))); }, [current]);
  const chosen = offerings.find((o) => o.offering_key === offeringKey) ?? null;
  const dues = noticeDue(cutover, windows);
  const tooSoon = dues.find((d) => d.tooSoon) ?? null;
  const editable = canWrite && (!t || t.state === 'draft');

  const verify = async (o: CatalogOfferingView) => {
    try { await ensureTargetService(slug, program, { offering_key: o.offering_key, verified: true }); await props.refresh(); props.say(`${o.offering_name} marked verified`, `${o.offering_name}: FedRAMP ${o.impact_level} · package ${o.fedramp_package_id} recorded from the Marketplace listing`); }
    catch (e) { props.setError(errMsg(e, 'The provider could not be marked verified.')); }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (props.busy) return;
    if (!chosen) { props.setError('Choose a target provider.'); return; }
    if (!cutover) { props.setError('Choose the planned cutover date.'); return; }
    props.setBusy(true); props.setError(null);
    try {
      const svc = (await ensureTargetService(slug, program, { offering_key: chosen.offering_key })).service;
      const start = `${cutover}T00:00:00.000Z`;
      if (!t) await createTransition(slug, program, { title: `Move to ${chosen.offering_name}`, target_external_service_id: svc.external_service_id, catalog_offering_key: chosen.offering_key, planned_cutover_start: start });
      else await updateTransition(slug, program, { title: `Move to ${chosen.offering_name}`, target_external_service_id: svc.external_service_id, catalog_offering_key: chosen.offering_key, planned_cutover_start: start });
      // Every component-boundary program that holds a moving component takes part.
      const joined = new Set([program, ...(t?.participants.map((p) => p.program) ?? [])]);
      const participants: string[] = [];
      for (const [i, p] of boundaryPrograms.entries()) {
        const holds = (standing[i] ?? []).some((id) => moving.has(id));
        if (!holds && p !== program) continue;
        if (!joined.has(p)) { try { await joinTransition(slug, p); } catch (err) { props.setError(errMsg(err, `${programLabel(p)} could not join.`)); continue; } }
        participants.push(p);
      }
      // A component whose hosting changes is a new register row that replaces it (ADR-0069 §1).
      const byKey = new Map(all.map((c) => [c.component_key, c]));
      const choices = new Map<string, MoveChoice>();
      for (const id of moving) {
        const c = all.find((x) => x.component_id === id);
        if (!c) continue;
        const key = targetKey(c.component_key, chosen.offering_key);
        let target = byKey.get(key) ?? null;
        if (!target) {
          target = (await createComponent(slug, program, {
            component_key: key, function: c.function, kind: c.kind, inherited_from_service_id: svc.external_service_id,
            federal_data: c.federal_data, security_category: c.security_category, is_virtual: c.is_virtual, is_public: c.is_public,
          })).component;
        }
        choices.set(id, { mode: 'move', target_component_id: target.component_id });
      }
      for (const p of participants) {
        const i = boundaryPrograms.indexOf(p);
        await setTargetScope(slug, p, targetScope(standing[i] ?? [], choices));
        await generateProposals(slug, p);
      }
      await props.refresh();
      props.onDone(participants.includes(program) ? program : participants[0] ?? program);
    } catch (err) { props.setError(errMsg(err, 'The move could not be saved.')); }
    finally { props.setBusy(false); }
  };

  return (
    <Card>
      <form className="gp-tr-form" onSubmit={submit} aria-labelledby="st-h" noValidate>
        <h2 id="st-h" className="gp-card-h">Target provider</h2>
        {props.catalogLoading ? <Spinner label="Loading providers" /> : offerings.length === 0
          ? <EmptyState title="No provider offerings reviewed yet" action={<Link to={`${prefix}/programs/${program}/scope`}>Back to the boundary</Link>} />
          : (
            <fieldset className="gp-tr-prov">
              <legend className="sr-only">Target provider</legend>
              {offerings.map((o) => (
                <div key={o.offering_key} className="gp-tr-tile">
                  <label className="gp-tr-choice">
                    <input type="radio" name="offering" value={o.offering_key} checked={offeringKey === o.offering_key} disabled={!editable} onChange={() => setOfferingKey(o.offering_key)} />
                    <span className="gp-tr-choice-t">
                      <b>{o.offering_name}</b>
                      <span className="gp-dim">{`FedRAMP ${o.impact_level[0]!.toUpperCase()}${o.impact_level.slice(1)} · ${o.services_in_scope.includes('*') ? 'all services' : `${o.services_in_scope.length} services`}`}</span>
                      {o.workspace_service?.verified
                        ? <span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>{`Verified ${shortDate(o.workspace_service.updated_at)}`}</span>
                        : <span className="gp-cb-tag">Provider data unverified</span>}
                    </span>
                  </label>
                  {!o.workspace_service?.verified ? (
                    <details className="gp-cb-why"><summary>{`Verify ${o.offering_name}`}</summary>
                      <span className="gp-tr-verify">
                        <a href={o.marketplace_url} target="_blank" rel="noopener noreferrer">Open FedRAMP Marketplace listing <span aria-hidden="true">↗</span><span className="sr-only">(opens in a new tab)</span></a>
                        <span className="gp-dim">{`Check: ${o.impact_level} · package ${o.fedramp_package_id}`}</span>
                        {canWrite ? <Button compact onClick={() => void verify(o)}>Mark verified</Button> : null}
                      </span>
                    </details>
                  ) : null}
                </div>
              ))}
            </fieldset>
          )}

        <div className="gp-cb-head"><h2 id="mv-h" className="gp-card-h">Components that move</h2><span className="gp-dim">{`${moving.size} of ${current.length} selected`}</span></div>
        {current.length === 0
          ? <EmptyState title="No components in a boundary yet" action={<Link to={`${prefix}/programs/${program}/scope`}>Add components to the boundary</Link>} />
          : (
            <div role="group" aria-labelledby="mv-h" className="gp-tr-comps">
              {current.map((id) => {
                const c = all.find((x) => x.component_id === id);
                if (!c) return null;
                const on = moving.has(id);
                return (
                  <label key={id} className="gp-tr-comp">
                    <input type="checkbox" checked={on} disabled={!editable} onChange={() => setMoving((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; })} />
                    <span><b>{c.component_key}</b> <span className="gp-dim">{`· ${KIND_LABEL[c.kind]} · ${providerLabel(c, names)}`}{on && chosen ? <> <span aria-hidden="true">→</span><span className="sr-only">moves to</span> {chosen.offering_name}</> : ' · stays'}</span></span>
                  </label>
                );
              })}
            </div>
          )}

        <Field id="cut" label="Planned cutover" error={tooSoon ? `Too soon. Earliest cutover is ${longDate(tooSoon.earliest!)}.` : undefined}>
          <Input type="date" value={cutover} disabled={!editable} onChange={(e) => setCutover(e.currentTarget.value)} />
        </Field>
        <div aria-live="polite" className="gp-tr-dues">
          {dues.map((d) => <p key={d.program} className="gp-tr-due">{d.due ? `${d.label} notice due by ${longDate(d.due)} · ${d.rule}` : `${d.label} notice · ${d.rule}`}</p>)}
        </div>
        <div className="gp-actions">
          {editable ? <Button type="submit" variant="primary" aria-disabled={props.busy || !chosen || !cutover || !moving.size || undefined}>{props.busy ? 'Working out what changes…' : 'See what changes'}</Button> : null}
          {!canWrite ? <span className="gp-dim">The program lead or an admin plans the move.</span> : t && t.state !== 'draft' ? <Link to="?step=changes">Open what changes</Link> : null}
          <span className="gp-dim">Nothing changes in your programs until cutover.</span>
        </div>
      </form>
    </Card>
  );
}

// ---- 2 What changes ---------------------------------------------------------------------------------

function ChangesStep(props: {
  slug: string; prefix: string; transitionId: string; state: string; participants: string[]; active: string; canWrite: boolean; all: BoundaryComponent[];
  names: ReadonlyMap<string, string>; offeringKey: string | null; targetServiceId: string; gateSha: string | null;
  /** V105-6 F-B1: the target plan this move waits for (its feasibility checks), when a plan opened the move. */
  planGate?: { plan_id: string; state: string; program: string | null } | null;
  setError: (e: string | null) => void; refresh: () => Promise<unknown>; say: (m: string, d?: string) => void; onPlanned: () => void; onTab: (p: string) => void;
}) {
  const { slug, prefix, transitionId, participants, active, canWrite, all, names } = props;
  const program = participants.includes(active) ? active : participants[0]!;
  // A tab counts as viewed once it is actually open (not while the participant list is still loading).
  const [viewed, setViewed] = useState<Set<string>>(() => (participants.includes(active) ? markViewed(transitionId, active) : readViewed(transitionId)));
  useEffect(() => { if (participants.includes(active)) setViewed(markViewed(transitionId, active)); }, [transitionId, active, participants.join('|')]);
  const gaps = useQueries({ queries: participants.map((p) => ({ queryKey: ['transition', slug, p, 'gap'], queryFn: () => getGap(slug, p) })) });
  const scopes = useQueries({ queries: participants.map((p) => ({ queryKey: ['transition', slug, p, 'target-scope'], queryFn: () => getTargetScope(slug, p) })) });
  const standing = useQueries({ queries: participants.map((p) => ({ queryKey: ['boundary', slug, 'scope', p, null], queryFn: () => getScope(slug, p, null) })) });
  const svc = useQuery({ queryKey: ['transition', slug, program, 'services'], queryFn: () => listProgramExternalServices(slug, program) });
  const [picker, setPicker] = useState<string | null>(null);
  const [optimistic, setOptimistic] = useState<Record<string, 'replaced' | 'removed' | 'unchanged'>>({});
  const [busy, setBusy] = useState(false);
  const idx = participants.indexOf(program);
  const g = gaps[idx]?.data;
  const draft = props.state === 'draft';
  const editable = canWrite && draft;
  const totalOpen = gaps.reduce((n, q) => n + (q.data ? openProposals(q.data.gap) : 0), 0);
  const allViewed = participants.every((p) => viewed.has(p));
  const nextUnviewed = participants.find((p) => !viewed.has(p));
  const doc = (svc.data?.external_services ?? []).find((s) => s.external_service_id === props.targetServiceId)?.documents
    .filter((d) => !d.withdrawn_at && d.kind === 'fedramp_authorization').sort((a, b) => (a.covers_to < b.covers_to ? 1 : -1))[0] ?? null;

  if (!g) return gaps[idx]?.error ? <Alert variant="error"><p>The changes could not load.</p><Button onClick={() => void gaps[idx]?.refetch()}>Retry</Button></Alert> : <Spinner label="Working out what changes" />;
  const gap = g.gap;
  const groups = groupObjectives(gap);
  const comp = (id: string) => all.find((c) => c.component_id === id);
  const pending = openProposals(gap);

  // Inline "Change replacement": rewrite the draft target for every program; the gap recomputes, no milestone is created.
  const choose = async (currentId: string, choice: 'move' | 'drop' | 'keep') => {
    if (busy) return;
    setBusy(true); props.setError(null);
    setOptimistic((o) => ({ ...o, [currentId]: choice === 'move' ? 'replaced' : choice === 'drop' ? 'removed' : 'unchanged' }));
    try {
      const c = comp(currentId)!;
      let target: BoundaryComponent | undefined;
      if (choice === 'move') {
        const key = targetKey(c.component_key, props.offeringKey ?? 'target');
        target = all.find((x) => x.component_key === key) ?? (await createComponent(slug, program, {
          component_key: key, function: c.function, kind: c.kind, inherited_from_service_id: props.targetServiceId, federal_data: c.federal_data, security_category: c.security_category,
          is_virtual: c.is_virtual, is_public: c.is_public,
        })).component;
      }
      for (const [i, p] of participants.entries()) {
        const st = standing[i]?.data?.scope.components.map((x) => x.component_id) ?? [];
        if (!st.includes(currentId)) continue;
        const choices = choicesFrom(st, scopes[i]?.data?.scope ?? []);
        choices.set(currentId, choice === 'move' ? { mode: 'move', target_component_id: target!.component_id } : { mode: choice });
        await setTargetScope(slug, p, targetScope(st, choices));
        await generateProposals(slug, p);
      }
      await props.refresh();
      setPicker(null);
      const provider = providerLabel(c, names).replace(/^Inherited from /, '');
      props.say(`${c.component_key} · ${choice === 'keep' ? `stays on ${provider}` : choice === 'drop' ? 'leaves the boundary' : `moves to ${target!.component_key}`}`, `${c.component_key}: changes and plan updated · no milestones added`);
    } catch (e) { props.setError(errMsg(e, 'The replacement could not change.')); }
    finally { setBusy(false); setOptimistic((o) => { const n = { ...o }; delete n[currentId]; return n; }); }
  };

  const confirmGroup = async (rows: TransitionGap['objectives'], keep: boolean) => {
    props.setError(null);
    try {
      for (const o of rows) {
        if (o.proposal?.status !== 'proposed') continue;
        const fresh = (await getGap(slug, program)).gap;
        if (!keep && o.party_after !== 'customer' && !doc) { props.setError('Record the target provider\'s authorization first (below).'); return; }
        await decideProposal(slug, program, o.proposal.proposal_id, {
          action: 'confirm', gap_sha256: fresh.gap_sha256,
          ...(keep ? { override: { party: 'customer' } } : { attestation_document_id: o.party_after === 'customer' ? null : doc!.document_id }),
        });
      }
      await props.refresh();
      props.say(keep ? `Kept ${requirementLabel(rows[0]!.requirement_id)} with you` : `Confirmed ${requirementLabel(rows[0]!.requirement_id)}`);
    } catch (e) { props.setError(errMsg(e, 'The proposal could not be decided.')); }
  };

  const plan = async () => {
    if (!props.gateSha) return;
    setBusy(true); props.setError(null);
    try { await planTransition(slug, program, props.gateSha); await props.refresh(); props.onPlanned(); }
    catch (e) {
      // F-B1: the move waits for its target plan; refreshing shows the wait and the link in place of the button.
      // The footer explains the wait once refreshed; no error alert.
      if ((e as { body?: { code?: string } }).body?.code === 'plan_not_locked') await props.refresh();
      else props.setError(errMsg(e, 'The move could not be scheduled.'));
    }
    finally { setBusy(false); }
  };

  const milestoneFor = (componentId: string, kind: ProjectedMilestone['kind'], requirementId?: string) =>
    g.milestones.find((x) => x.component_id === componentId && x.kind === kind && (!requirementId || x.requirement_id === requirementId)) ?? null;
  const notNeeded = g.milestones.filter((x) => x.status === 'not_needed');
  // GovRAMP's Provisional cap (ADR-0066 §7): an unverified supplier in the boundary. Lifted when every one leaves; stays while one remains.
  const svcList = svc.data?.external_services ?? [];
  const unverified = (id: string | null) => { const x = svcList.find((y) => y.external_service_id === id); return !!x && x.fedramp_status !== 'authorized' && !(x.govramp_status === 'authorized' && x.govramp_status_checked_at); };
  const supplierOf = (c: BoundaryComponent | undefined) => c?.external_service_id ?? null;
  const curUnverified = (standing[idx]?.data?.scope.components ?? []).filter((c) => unverified(supplierOf(c)));
  const tgtUnverified = gap.components.filter((r) => r.change !== 'removed' && unverified(supplierOf(comp(r.component_id))));
  const capLifted = gap.program === 'govramp' && curUnverified.length > 0 && tgtUnverified.length === 0;
  const capAdded = gap.program === 'govramp' && tgtUnverified.length > 0;

  return (
    <div className="gp-tr">
      <nav className="gp-tr-tabs" aria-label="Program">
        <ul>
          {participants.map((p) => (
            <li key={p}><Link to={`?step=changes&p=${p}`} aria-current={p === program ? 'page' : undefined} onClick={(e) => { e.preventDefault(); props.onTab(p); }}>
              {programLabel(p)}{viewed.has(p) ? <span className="gp-dim"> <span aria-hidden="true">✓</span> viewed</span> : null}
            </Link></li>
          ))}
        </ul>
        <span className="gp-dim">{`${participants.filter((p) => viewed.has(p)).length} of ${participants.length} programs viewed · ${totalOpen} proposal${totalOpen === 1 ? '' : 's'} open`}</span>
      </nav>

      {pending ? (
        <Alert variant="warning"><p><b>{`${pending} inheritance proposal${pending === 1 ? '' : 's'} need review.`}</b> Not counted until confirmed.</p>
          <a className="gp-linkbtn gp-linkbtn--compact" href="#inh-h" onClick={() => window.setTimeout(() => document.getElementById('inh-h')?.focus(), 0)}>{`Review ${pending}`}</a></Alert>
      ) : null}
      {gap.incomplete.map((i) => <Alert key={i.field} variant="warning"><p><b>{i.message}</b> {i.action}</p><Link to={`${prefix}/programs/${program}`}>{`Open ${programLabel(program)}`}</Link></Alert>)}
      {gap.blockers.filter((b) => ['impact_below_program', 'impact_unknown', 'component_facts_missing'].includes(b.code)).map((b) => (
        <Alert key={`${b.code}:${b.ref ?? ''}`} variant="error"><p>{b.message}</p>{b.code === 'component_facts_missing' ? <Link to={`${prefix}/programs/${program}/scope?view=issues`}>Answer in the boundary</Link> : <Link to="?step=target">Change the target</Link>}</Alert>
      ))}

      <Card>
        <h3 className="gp-card-h" id="rd-h">{`Readiness · ${programLabel(program)}`}</h3>
        <div className="gp-tr-vs">
          <div><span className="gp-dim">Today</span><b>{`${gap.readiness.current.met} of ${gap.readiness.current.total}`}</b><span className="gp-dim">met</span></div>
          <div data-part="after"><span className="gp-dim">After the move · projected</span><b>{`${gap.readiness.target.met} of ${gap.readiness.target.total}`}</b>
            <span className="gp-dim">{[gap.readiness.needs_assessment.length ? `${gap.readiness.needs_assessment.length} need fresh assessment` : '', pending ? `${pending} wait on confirmation` : ''].filter(Boolean).join(' · ') || 'Nothing waits on confirmation'}</span></div>
        </div>
        {capLifted ? <p className="gp-tr-line"><span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>Provisional cap lifted</span> <span className="gp-dim">the unverified supplier leaves the boundary</span></p> : null}
        {capAdded ? <p className="gp-tr-line"><span className="gp-cb-chip" data-tone="bad"><span aria-hidden="true">✕ </span>Caps GovRAMP at Provisional</span> <span className="gp-dim">{`${tgtUnverified.map((r) => r.component_key).join(', ')} keeps an unverified supplier`}</span>{editable ? <Link to={`${prefix}/programs/${program}/scope?view=issues`}>Add verification</Link> : null}</p> : null}
      </Card>

      <div className="gp-tr-grid">
        <Card>
          <h3 className="gp-card-h" id="inh-h" tabIndex={-1}>{`Inheritance · ${programLabel(program)}`}</h3>
          {groups.length === 0 ? <p className="gp-dim">No responsibility moves for this program.</p> : (['gained', 'provider_changed', 'party_changed', 'lost'] as const).map((ch) => {
            const gs = collapseFamilies(groups.filter((x) => x.change === ch));
            if (!gs.length) return null;
            return (
              <section key={ch} aria-labelledby={`inh-${ch}`}>
                <h4 id={`inh-${ch}`} className="gp-tr-sub">{`${CHANGE_HEADING[ch]} · ${gs.length}`}</h4>
                <ul className="gp-cb-rows">
                  {gs.map((x) => {
                    const ms = ch === 'lost' ? g.milestones.find((mm) => mm.kind === 'loss' && mm.requirement_id === x.requirement_id) ?? null : null;
                    return (
                      <li key={`${ch}:${x.requirement_id}`} className="gp-cb-row">
                        <span className="gp-cb-main"><b>{x.members.length > 1 ? x.label : `${x.label}${x.rows.length > 1 ? ` · ${x.rows.length} objectives` : ''}`}</b>
                          <span className="gp-cb-meta">
                            {x.proposed ? <span className="gp-cb-chip" data-tone="warn">Proposed</span> : <span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>Confirmed</span>}
                            {x.unverified ? <span className="gp-cb-tag">Unverified catalog row</span> : null}
                            {x.rows[0]?.service_after ? <span>{names.get(x.rows[0].service_after) ?? 'Provider'}</span> : null}
                            {ms ? <span>{`Milestone · ${projectedLabel(ms)}`}</span> : null}
                          </span>
                          {x.members.length > 1 ? <details className="gp-cb-why"><summary>{`Show ${x.members.length} controls`}</summary><span>{x.members.join(' · ')}</span></details> : null}
                        </span>
                        <span className="gp-cb-act">
                          {x.proposed && editable ? <>
                            <Button compact onClick={() => void confirmGroup(x.rows, false)} aria-label={`${x.rows.length > 1 ? `Confirm ${x.proposed}` : 'Confirm'} · ${x.label}`}>{x.rows.length > 1 ? `Confirm ${x.proposed}` : 'Confirm'}</Button>
                            {ch !== 'lost' ? <Button compact variant="ghost" onClick={() => void confirmGroup(x.rows, true)} aria-label={`Keep with you · ${x.label}`}>Keep with you</Button> : null}
                          </> : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
          {pending && !doc && editable ? <AttestationForm slug={slug} program={program} serviceId={props.targetServiceId} onSaved={async () => { await props.refresh(); props.say('Authorization recorded'); }} setError={props.setError} /> : null}
        </Card>

        <div className="gp-tr">
          <Card>
            <h3 className="gp-card-h" id="cmp-h">{`Components · ${gap.components.filter((c) => c.change !== 'unchanged').length} change`}</h3>
            <ul className="gp-cb-rows" aria-labelledby="cmp-h">
              {(standing[idx]?.data?.scope.components ?? []).map((cur) => {
                const row = gap.components.find((r) => r.replaces?.component_id === cur.component_id) ?? gap.components.find((r) => r.component_id === cur.component_id) ?? null;
                const change = optimistic[cur.component_id] ?? (row?.replaces?.component_id === cur.component_id ? 'replaced' : row?.change ?? 'removed');
                const provider = providerLabel(cur, names).replace(/^Inherited from /, '');
                const open = picker === cur.component_id;
                const pid = `pick-${cur.component_id}`;
                return (
                  <li key={cur.component_id} className="gp-cb-row">
                    <span className="gp-cb-main"><span><b>{cur.component_key}</b> {change === 'replaced' && row ? <><span aria-hidden="true">→</span><span className="sr-only">becomes</span> {row.component_key}</> : change === 'removed' ? <span className="gp-dim">leaves the boundary</span> : <span className="gp-dim">{`stays on ${provider}`}</span>}</span>
                      {open ? (
                        <fieldset className="gp-tr-pick" id={pid}>
                          <legend className="gp-tr-sub">{`Replace ${cur.component_key} with`}</legend>
                          <label className="gp-tr-choice"><input type="radio" name={pid} checked={change === 'replaced'} disabled={busy} onChange={() => void choose(cur.component_id, 'move')} /><span><b>{targetKey(cur.component_key, props.offeringKey ?? 'target')}</b> <span className="gp-dim">inherited from the target provider</span></span></label>
                          <label className="gp-tr-choice"><input type="radio" name={pid} checked={change === 'removed'} disabled={busy} onChange={() => void choose(cur.component_id, 'drop')} /><span><b>Another component takes over</b> <span className="gp-dim">it leaves the boundary</span></span></label>
                          <label className="gp-tr-choice"><input type="radio" name={pid} checked={change === 'unchanged'} disabled={busy} onChange={() => void choose(cur.component_id, 'keep')} /><span><b>{`Keep ${provider}`}</b> <span className="gp-dim">it stays as it is</span></span></label>
                          <span className="gp-dim">Updates this view and the plan · no milestones added</span>
                        </fieldset>
                      ) : null}
                    </span>
                    {editable ? <Button compact variant="ghost" aria-expanded={open} aria-controls={open ? pid : undefined} onClick={() => setPicker(open ? null : cur.component_id)} aria-label={`Change replacement · ${cur.component_key}`}>Change</Button> : null}
                  </li>
                );
              })}
            </ul>
          </Card>
          <Card>
            <h3 className="gp-card-h" id="tp-h">{`Third parties · ${gap.tprs.removed.length} removed · ${gap.tprs.added.length} added`}</h3>
            <ul className="gp-cb-rows" aria-labelledby="tp-h">
              {gap.tprs.removed.map((s) => {
                const by = gap.components.find((c) => (c.replaces && comp(c.replaces.component_id) && [comp(c.replaces.component_id)!.external_service_id, comp(c.replaces.component_id)!.inherited_from_service_id].includes(s.external_service_id)));
                const ms = by?.replaces ? milestoneFor(by.replaces.component_id, 'switch') : null;
                return <li key={s.external_service_id} className="gp-cb-row"><span className="gp-cb-main"><b>{s.name}</b><span className="gp-cb-meta"><span>Leaves the boundary</span>{ms ? <span>{`Milestone · ${projectedLabel(ms)}`}</span> : null}</span></span></li>;
              })}
              {gap.tprs.added.map((s) => (
                <li key={s.external_service_id} className="gp-cb-row"><span className="gp-cb-main"><b>{s.name}</b><span className="gp-cb-meta"><span>Joins the boundary</span>
                  {s.fedramp_status === 'authorized' ? <span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>FedRAMP authorized</span> : <span className="gp-cb-tag">Not verified</span>}
                  {s.missing_tpr_fields.length ? <span>{`${s.missing_tpr_fields.length} third-party fields to fill`}</span> : null}</span></span></li>
              ))}
            </ul>
          </Card>
          {notNeeded.length ? (
            <Card>
              <h3 className="gp-card-h" id="nn-h">{`Planned earlier · ${notNeeded.length} not needed`}</h3>
              <ul className="gp-cb-rows" aria-labelledby="nn-h">
                {notNeeded.map((x) => (
                  <li key={x.work_item_id ?? x.key} className="gp-cb-row"><span className="gp-cb-main"><b>{x.title}</b><span className="gp-cb-meta"><span className="gp-cb-chip">Not needed</span><span>{`${x.component_key} stays`}</span></span></span>
                    {editable ? <button type="button" className="gp-cb-textbtn" onClick={() => setPicker(x.component_id)} aria-label={`Change · ${x.component_key}`}>Change</button> : null}</li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>

      <div className="gp-tr-footer">
        <span className="gp-dim">{allViewed ? `Every program viewed · ${g.milestones.filter((x) => x.status === 'new').length} milestones come from losses and removals` : `Next: review what changes for ${programLabel(nextUnviewed!)}`}</span>
        {!allViewed ? <Button variant="primary" onClick={() => props.onTab(nextUnviewed!)}>{`Next: ${programLabel(nextUnviewed!)}`}</Button>
          : props.state === 'draft' && props.planGate ? (
            <span className="gp-actions">
              {/* V105-7-2: the gate lifts once the plan is locked (the server returns no plan_gate then). */}
              <span className="gp-cb-chip gp-pv-chip" data-tone="warn" role="status">{planGateText(props.planGate)}</span>
              <Link className="gp-linkbtn" data-variant="primary" to={`${prefix}/programs/${props.planGate.program ?? program}/scope?view=plan`}>Open the target plan</Link>
            </span>
          )
          : props.state === 'draft' ? (canWrite ? <Button variant="primary" aria-disabled={busy || !props.gateSha || undefined} onClick={() => void plan()}>{busy ? 'Scheduling the move…' : 'Schedule the move'}</Button> : <span className="gp-dim">The program lead or an admin schedules the move.</span>)
          : <Link className="gp-linkbtn" data-variant="primary" to="?step=plan">Open the schedule</Link>}
      </div>
    </div>
  );
}

function AttestationForm({ slug, program, serviceId, onSaved, setError }: { slug: string; program: string; serviceId: string; onSaved: () => Promise<void>; setError: (e: string | null) => void }) {
  const [from, setFrom] = useState(''); const [to, setTo] = useState(''); const [ref, setRef] = useState('');
  return (
    <form className="gp-inline-form" aria-label="The target provider's authorization" onSubmit={async (e) => {
      e.preventDefault(); if (!from || !to || !ref.trim()) { setError('Give the authorization dates and its package ID.'); return; }
      try { await addCoverageDocument(slug, program, serviceId, { kind: 'fedramp_authorization', covers_from: from, covers_to: to, reference: ref.trim() }); await onSaved(); }
      catch (err) { setError(errMsg(err, 'The authorization could not be recorded.')); }
    }}>
      <p className="gp-dim">Confirming provider-held rows needs the provider&apos;s FedRAMP authorization.</p>
      <Field id="att-ref" label="Package ID"><Input value={ref} onChange={(e) => setRef(e.currentTarget.value)} /></Field>
      <Field id="att-from" label="Authorized from"><Input type="date" value={from} onChange={(e) => setFrom(e.currentTarget.value)} /></Field>
      <Field id="att-to" label="Valid to"><Input type="date" value={to} onChange={(e) => setTo(e.currentTarget.value)} /></Field>
      <div className="gp-actions"><Button type="submit">Record authorization</Button></div>
    </form>
  );
}

// ---- 3 Plan -----------------------------------------------------------------------------------------

function PlanStep(props: {
  slug: string; prefix: string; program: string; transition: { transition_id: string; state: string; planned_cutover_start: string }; participants: Array<{ profile_id: string; program: string }>;
  canWrite: boolean; members: Array<{ user_id: string; display_name: string | null; email: string }>; setError: (e: string | null) => void; refresh: () => Promise<unknown>;
  say: (m: string, d?: string) => void; gateSha: string | null; onNext: () => void;
}) {
  const { slug, prefix, program, transition: t, participants, canWrite, members } = props;
  const plan = useQuery({ queryKey: ['transition', slug, program, 'plan'], queryFn: () => getPlan(slug, program) });
  const gaps = useQueries({ queries: participants.map((p) => ({ queryKey: ['transition', slug, p.program, 'gap'], queryFn: () => getGap(slug, p.program) })) });
  const [edit, setEdit] = useState<{ id: string; field: 'owner' | 'date' } | null>(null);
  const name = (id: string) => { const m = members.find((x) => x.user_id === id); return m ? m.display_name || m.email : 'Member'; };
  const progOf = (profileId: string | null) => participants.find((p) => p.profile_id === profileId)?.program ?? null;
  if (t.state === 'draft') {
    return <EmptyState title="The schedule is built from the reviewed changes" action={<Link to="?step=changes">Review what changes</Link>} />;
  }
  if (plan.isLoading) return <Spinner label="Loading the plan" />;
  if (plan.error || !plan.data) return <Alert variant="error"><p>The schedule could not load.</p><Button onClick={() => void plan.refetch()}>Retry</Button></Alert>;
  const notNeeded = gaps.flatMap((q) => q.data?.milestones.filter((x) => x.status === 'not_needed') ?? []);
  const save = async (it: PlanItemView, body: { owner_user_id?: string; scheduled_for?: string }) => {
    try { await updatePlanItem(slug, program, it.plan_item_id, body); await props.refresh(); setEdit(null); props.say(body.owner_user_id ? `Owner changed · ${it.title}` : `Date changed · ${it.title}`); }
    catch (e) { props.setError(errMsg(e, 'The milestone could not change.')); }
  };
  const action = (it: PlanItemView) => {
    const p = progOf(it.profile_id);
    if (it.milestone_key === 'confirm_responsibilities') return <Link className="gp-linkbtn gp-linkbtn--compact" to={`?step=changes&p=${p ?? ''}`}>Review proposals</Link>;
    if (/^send_|update_service_docs/.test(it.milestone_key)) return <Link className="gp-linkbtn gp-linkbtn--compact" to="?step=reporting">Review draft</Link>;
    if (it.milestone_key === 'cutover') return <Link className="gp-linkbtn gp-linkbtn--compact" to="?step=reporting#cv-h">See what&apos;s blocking</Link>;
    return <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/items/work_item/${encodeURIComponent(it.work_item_id)}`} aria-label={`Open · ${it.title}`}>Open</Link>;
  };
  return (
    <Card>
      <div className="gp-cb-head"><h2 className="gp-card-h" id="pl-h">{`Milestones · ${plan.data.plan.length}`}</h2></div>
      <p className="gp-dim">{`Counted back from cutover ${shortDate(t.planned_cutover_start)} · not POA&M items`}</p>
      <table className="gp-table gp-tr-table" role="table" aria-labelledby="pl-h">
        <thead role="rowgroup"><tr role="row">
          <th role="columnheader" scope="col">Milestone</th><th role="columnheader" scope="col">Owner</th><th role="columnheader" scope="col">Date</th>
          <th role="columnheader" scope="col">Status</th><th role="columnheader" scope="col">Action</th>
        </tr></thead>
        <tbody role="rowgroup">
          {plan.data.plan.map((it) => {
            const st = planStatus(it);
            const p = progOf(it.profile_id);
            return (
              <tr role="row" key={it.plan_item_id}>
                <th role="rowheader" scope="row" className="gp-tr-ms"><b>{it.title}</b> {p ? <span className="gp-cb-tag">{programLabel(p)}</span> : null} {it.carried ? <span className="gp-cb-chip" data-tone="info">Already planned</span> : null}</th>
                <td role="cell" data-label="Owner">
                  {edit?.id === it.plan_item_id && edit.field === 'owner'
                    ? <InlineSelect label={`Owner, ${it.title}`} value={it.owner_user_ids[0] ?? ''} options={members.map((m) => ({ value: m.user_id, label: m.display_name || m.email }))} onSave={(v) => void save(it, { owner_user_id: v })} onCancel={() => setEdit(null)} />
                    : canWrite && it.work_item_status === 'open'
                      ? <button type="button" className="gp-tr-cell" onClick={() => setEdit({ id: it.plan_item_id, field: 'owner' })} aria-label={`${it.owner_user_ids.map(name).join(', ') || 'Unassigned'}, ${it.title} · change owner`}>{it.owner_user_ids.map(name).join(', ') || 'Unassigned'}</button>
                      : it.owner_user_ids.map(name).join(', ') || 'Unassigned'}
                </td>
                <td role="cell" data-label="Date">
                  {edit?.id === it.plan_item_id && edit.field === 'date'
                    ? <InlineDate label={`Date, ${it.title}`} value={(it.scheduled_for ?? '').slice(0, 10)} onSave={(v) => void save(it, { scheduled_for: v })} onCancel={() => setEdit(null)} />
                    : canWrite && it.work_item_status === 'open'
                      ? <button type="button" className="gp-tr-cell" onClick={() => setEdit({ id: it.plan_item_id, field: 'date' })} aria-label={`${it.scheduled_for ? shortDate(it.scheduled_for) : 'No date'}, ${it.title} · change date`}>{it.scheduled_for ? shortDate(it.scheduled_for) : 'No date'}</button>
                      : it.scheduled_for ? shortDate(it.scheduled_for) : 'No date'}
                </td>
                <td role="cell" data-label="Status"><span className="gp-cb-chip" data-tone={st.tone}>{st.text}</span></td>
                <td role="cell" data-label="Action">{action(it)}</td>
              </tr>
            );
          })}
          {notNeeded.map((x) => (
            <tr role="row" key={`nn:${x.work_item_id}`}>
              <th role="rowheader" scope="row" className="gp-tr-ms"><b>{x.title}</b> <span className="gp-cb-tag">Planned earlier</span></th>
              <td role="cell" data-label="Owner"><span className="gp-dim">—</span></td>
              <td role="cell" data-label="Date">{x.due_at ? shortDate(x.due_at) : '—'}</td>
              <td role="cell" data-label="Status"><span className="gp-cb-chip">Not needed</span></td>
              <td role="cell" data-label="Action"><Link to={`?step=changes`} aria-label={`Change · ${x.component_key}`}>Change</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="gp-actions"><Button variant="primary" onClick={props.onNext}>Open reports &amp; cutover</Button></div>
    </Card>
  );
}

function InlineSelect({ label, value, options, onSave, onCancel }: { label: string; value: string; options: Array<{ value: string; label: string }>; onSave: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value);
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return <span className="gp-tr-inline"><Select ref={ref} aria-label={label} value={v} onChange={(e) => setV(e.currentTarget.value)} onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</Select>
    <Button compact onClick={() => onSave(v)}>Save</Button><Button compact variant="ghost" onClick={onCancel}>Cancel</Button></span>;
}
function InlineDate({ label, value, onSave, onCancel }: { label: string; value: string; onSave: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return <span className="gp-tr-inline"><Input ref={ref} type="date" aria-label={label} value={v} onChange={(e) => setV(e.currentTarget.value)} onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }} />
    <Button compact onClick={() => v && onSave(v)}>Save</Button><Button compact variant="ghost" onClick={onCancel}>Cancel</Button></span>;
}

// ---- 4 Reports & cutover ----------------------------------------------------------------------------

function ReportingStep(props: {
  slug: string; prefix: string; program: string; transition: { transition_id: string; state: string; planned_cutover_start: string }; participants: Array<{ profile_id: string; program: string; significant_change_id: string | null }>;
  canWrite: boolean; canCutover: boolean; gate: { ready: boolean; combined_sha256: string; pinned_sha256: string | null; blockers: Array<{ profile_id: string; code: string; message: string; ref?: string }> } | null;
  setError: (e: string | null) => void; refresh: () => Promise<unknown>; say: (m: string, d?: string) => void; role: string;
}) {
  const { slug, prefix, transition: t, participants, canWrite, canCutover, gate } = props;
  const [busy, setBusy] = useState(false);
  if (t.state === 'draft') return <EmptyState title="Reports are drafted when the plan is built" action={<Link to="?step=changes">Review what changes</Link>} />;
  const blockers = gate?.blockers ?? [];
  const ready = !!gate?.ready;
  const cut = async (which: 'start' | 'complete') => {
    if (!gate || busy) return;
    setBusy(true); props.setError(null);
    try { await (which === 'start' ? startCutover : completeCutover)(slug, props.program, gate.combined_sha256); await props.refresh(); props.say(which === 'start' ? 'Cutover started' : 'Cutover complete', which === 'start' ? 'Both boundaries are now reported until the cutover completes.' : 'The target is now the standing boundary.'); }
    catch (e) { props.setError(errMsg(e, 'The cutover was refused.')); }
    finally { setBusy(false); }
  };
  const replan = async () => {
    if (!gate) return;
    try { await planTransition(slug, props.program, gate.combined_sha256); await props.refresh(); props.say('Schedule rebuilt from the current changes'); }
    catch (e) { props.setError(errMsg(e, 'The schedule could not be rebuilt.')); }
  };
  return (
    <div className="gp-tr">
      <Card>
        <h2 className="gp-card-h" id="rp-h">Drafts</h2>
        <ul className="gp-cb-rows" aria-labelledby="rp-h">
          {participants.map((p) => p.program === 'fedramp-cr26'
            ? <ScnRow key={p.profile_id} slug={slug} prefix={prefix} program={p.program} changeId={p.significant_change_id} />
            : p.program === 'govramp' ? <NoticeRows key={p.profile_id} slug={slug} program={p.program} canWrite={canWrite} setError={props.setError} refresh={props.refresh} say={props.say} /> : null)}
          {participants.map((p) => <DraftRow key={`d:${p.profile_id}`} slug={slug} program={p.program} canWrite={canWrite} setError={props.setError} refresh={props.refresh} say={props.say} />)}
        </ul>
      </Card>

      <section className="gp-tr-gate" aria-labelledby="cv-h">
        <div className="gp-cb-head">
          <h2 className="gp-card-h" id="cv-h" tabIndex={-1}>Cutover readiness</h2>
          {t.state === 'completed' ? <span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>Complete</span>
            : ready ? <span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>Ready</span>
            : <span className="gp-cb-chip" data-tone="bad"><span aria-hidden="true">✕ </span>{`Blocked · ${blockers.length} open`}</span>}
        </div>
        <p className="gp-dim">Every provider-held objective confirmed and attested, in every program. There is no override.</p>
        {blockers.some((b) => b.code === 'gap_changed_since_plan') ? (
          <Alert variant="warning"><p>The changes moved since the plan was built.</p>{canWrite ? <Button compact onClick={() => void replan()}>Rebuild the plan</Button> : null}</Alert>
        ) : null}
        <div className="gp-tr-gp-grid">
          {participants.map((p) => {
            const mine = blockers.filter((b) => b.profile_id === p.profile_id);
            const confirm = mine.filter((b) => b.code === 'unconfirmed_proposal').length;
            const attest = mine.filter((b) => b.code === 'attestation_not_current').length;
            const other = mine.filter((b) => b.code !== 'unconfirmed_proposal' && b.code !== 'attestation_not_current');
            return (
              <div key={p.profile_id} className="gp-tr-gp">
                <b>{programLabel(p.program)}</b>
                {mine.length === 0 ? <span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>Every gate passes</span> : null}
                {confirm ? <span>{`${confirm} to confirm`}</span> : null}
                {attest ? <span>{`${attest} without a current attestation`}</span> : null}
                {other.map((b) => <span key={`${b.code}:${b.ref ?? ''}`} className="gp-dim">{b.message}</span>)}
                {mine.length ? <div className="gp-actions"><Link className="gp-linkbtn gp-linkbtn--compact" to={`?step=changes&p=${p.program}`}>{`Review ${confirm + attest || mine.length}`}</Link></div> : null}
              </div>
            );
          })}
        </div>
        <div className="gp-actions">
          {t.state === 'planned' ? <>
            <Button variant="primary" aria-disabled={!ready || !canCutover || busy || undefined} aria-describedby="cv-why" onClick={() => { if (ready && canCutover) void cut('start'); }}>Start cutover</Button>
            <span id="cv-why" className="gp-dim">{!canCutover ? 'An owner or admin starts the cutover.' : ready ? 'Both boundaries are reported until it completes.' : 'Opens when every gate passes.'}</span>
          </> : t.state === 'cutover' ? <>
            <Button variant="primary" aria-disabled={!ready || !canCutover || busy || undefined} aria-describedby="cv-why" onClick={() => { if (ready && canCutover) void cut('complete'); }}>Complete cutover</Button>
            <span id="cv-why" className="gp-dim">{!canCutover ? 'An owner or admin completes the cutover.' : 'Swaps the standing boundary and flips the confirmed inheritance in one step.'}</span>
          </> : null}
        </div>
      </section>
    </div>
  );
}

function ScnRow({ slug, prefix, program, changeId }: { slug: string; prefix: string; program: string; changeId: string | null }) {
  const scn = useQuery({ queryKey: ['transition', slug, program, 'scn'], queryFn: () => getScn(slug, program), enabled: !!changeId, retry: false });
  if (!changeId) return <li className="gp-cb-row"><span className="gp-cb-main"><b>FedRAMP significant change notice</b><span className="gp-cb-meta"><span>Filed when the plan is built</span></span></span></li>;
  const r = scn.data?.scn;
  return (
    <li className="gp-cb-row">
      <span className="gp-cb-main"><b>FedRAMP significant change notice</b>
        <span className="gp-cb-meta">
          {r ? (r.fedramp_schema.valid ? <span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>Matches FedRAMP&apos;s schema</span> : <span className="gp-cb-chip" data-tone="warn">{`${r.incomplete.length} field${r.incomplete.length === 1 ? '' : 's'} to decide`}</span>) : <span>Loading…</span>}
          {r?.incomplete.slice(0, 2).map((i) => <span key={i.field}>{i.message}</span>)}
        </span>
      </span>
      <span className="gp-cb-act">
        <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/items/scn/${encodeURIComponent(changeId)}`} aria-label="Review draft · FedRAMP notice">Review draft</Link>
        <a className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" href={scnDownloadHref(slug, program)} download aria-label="Download JSON · FedRAMP notice">Download JSON</a>
      </span>
    </li>
  );
}

function NoticeRows({ slug, program, canWrite, setError, refresh, say }: { slug: string; program: string; canWrite: boolean; setError: (e: string | null) => void; refresh: () => Promise<unknown>; say: (m: string) => void }) {
  const notices = useQuery({ queryKey: ['transition', slug, program, 'notices'], queryFn: () => listNotices(slug, program) });
  const [open, setOpen] = useState<string | null>(null);
  const [method, setMethod] = useState<'email' | 'portal' | 'other'>('email');
  const [sentOn, setSentOn] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  return <>
    {(notices.data?.notices ?? []).filter((n) => n.status !== 'cancelled').map((n) => (
      <li key={n.notice_id} className="gp-cb-row">
        <span className="gp-cb-main"><b>GovRAMP change notice</b>
          <span className="gp-cb-meta">
            <span>{n.status === 'sent' ? `Sent ${shortDate(n.delivered_on)}` : n.due_at ? `Send by ${shortDate(n.due_at)}` : 'Confirm the timing with your assessor'}</span>
            <span className="gp-cb-chip" data-tone={n.status === 'sent' ? 'ok' : 'info'}>{n.status === 'sent' ? 'Sent' : 'Draft ready'}</span>
          </span>
          {open === n.notice_id ? (
            <form className="gp-inline-form" aria-label="Record the GovRAMP notice as sent" onSubmit={async (e) => {
              e.preventDefault();
              try { await sendNotice(slug, program, n.notice_id, { confirm: true, sia_sha256: n.sia_sha256, delivery: { method, sent_on: sentOn, note: note.trim() || null } }); await refresh(); setOpen(null); say('GovRAMP notice recorded as sent'); }
              catch (err) { setError(errMsg(err, 'The notice could not be recorded.')); }
            }}>
              <Field id={`m-${n.notice_id}`} label="Sent by"><Select value={method} onChange={(e) => setMethod(e.currentTarget.value as typeof method)}><option value="email">Email</option><option value="portal">Portal</option><option value="other">Other</option></Select></Field>
              <Field id={`d-${n.notice_id}`} label="Sent on"><Input type="date" value={sentOn} onChange={(e) => setSentOn(e.currentTarget.value)} /></Field>
              {method === 'other' ? <Field id={`n-${n.notice_id}`} label="How"><Input value={note} onChange={(e) => setNote(e.currentTarget.value)} /></Field> : null}
              <div className="gp-actions"><Button type="submit" variant="primary">Record as sent</Button><Button variant="ghost" onClick={() => setOpen(null)}>Cancel</Button></div>
            </form>
          ) : null}
        </span>
        {n.status === 'draft' && canWrite ? <span className="gp-cb-act"><Button compact aria-expanded={open === n.notice_id} onClick={() => setOpen(open === n.notice_id ? null : n.notice_id)} aria-label="Record as sent · GovRAMP notice">Record as sent</Button></span> : null}
      </li>
    ))}
  </>;
}

function DraftRow({ slug, program, canWrite, setError, refresh, say }: { slug: string; program: string; canWrite: boolean; setError: (e: string | null) => void; refresh: () => Promise<unknown>; say: (m: string) => void }) {
  const drafts = useQuery({ queryKey: ['transition', slug, program, 'drafts'], queryFn: () => listDrafts(slug, program) });
  const list = drafts.data?.drafts ?? [];
  const last = list[list.length - 1] ?? null;
  return (
    <li className="gp-cb-row">
      <span className="gp-cb-main"><b>{`SSP and inventory · ${programLabel(program)} target draft`}</b>
        <span className="gp-cb-meta"><span>{last ? `Signed ${shortDate(last.created_at)}` : 'Not issued yet'}</span></span></span>
      {canWrite ? <span className="gp-cb-act"><Button compact onClick={async () => {
        try { await issueDraft(slug, program); await refresh(); say(`${programLabel(program)} target draft signed`); } catch (e) { setError(errMsg(e, 'The draft could not be issued.')); }
      }} aria-label={`${last ? 'Issue again' : 'Issue signed draft'} · ${programLabel(program)}`}>{last ? 'Issue again' : 'Issue signed draft'}</Button></span> : null}
    </li>
  );
}
